import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Cancel as CancelIcon, CheckCircle as CheckCircleIcon, Download as DownloadIcon, FileDownload as FileDownloadIcon, GridOn as GridOnIcon, Print as PrintIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ordersApi } from '@/api/orders';
import { tenantApi } from '@/api/tenant';
import type { Order, OrderItem, OrderStatus } from '@/types/order';
import { downloadExcelFromBackend } from '@/utils/excelExport';

function downloadCsv(order: Order, supplierName: string) {
  const headers = ['#', 'SKU', 'Product Name', 'Qty', 'Free Qty', 'Scheme', 'PTR', 'MRP Rate', 'Discount%', 'GST%', 'Total (PTR)'];
  const rows = (order.items ?? []).map((item: OrderItem, idx: number) => {
    const ptr = Number(item.unitPrice ?? item.ptr ?? 0);
    const total = ptr * Number(item.qty) - Number((item as any).discountAmt ?? 0);
    return [
      idx + 1, item.sku,
      `"${item.productName.replace(/"/g, '""')}"`,
      item.qty,
      Number(item.freeQty) > 0 ? item.freeQty : '',
      (item as any).scheme || '',
      ptr.toFixed(2),
      item.mrp != null ? Number(item.mrp).toFixed(2) : '',
      (item as any).discountPct ?? '', item.gstRate ?? '', total.toFixed(2),
    ];
  });
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `order-${order.orderNumber || order.id}-${supplierName}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadExcel(order: Order, supplierName: string) {
  downloadExcelFromBackend({
    title: `Order ${order.orderNumber ?? order.id}`,
    subTitle: `Supplier: ${supplierName} | Date: ${new Date().toLocaleDateString('en-IN')}`,
    headers: ['#', 'SKU', 'Product Name', 'Qty', 'Free Qty', 'Scheme', 'PTR', 'MRP Rate', 'Discount%', 'GST%', 'Total (PTR)'],
    rows: (order.items ?? []).map((item: OrderItem, idx: number) => {
      const ptr = Number(item.unitPrice ?? item.ptr ?? 0);
      const total = ptr * Number(item.qty) - Number((item as any).discountAmt ?? 0);
      return [
        idx + 1, item.sku, item.productName, item.qty,
        Number(item.freeQty) > 0 ? item.freeQty : '',
        (item as any).scheme || '',
        ptr.toFixed(2),
        item.mrp != null ? Number(item.mrp).toFixed(2) : '',
        (item as any).discountPct ?? '', item.gstRate ?? '', total.toFixed(2),
      ];
    }),
  });
}

function parseCheckItemsCsv(csvText: string, orderItems: OrderItem[]): ShortageEntry[] {
  const lines = csvText.trim().split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const header = lines[0].toLowerCase().split(',').map(h => h.replace(/"/g, '').trim());
  const skuIdx = header.findIndex(h => h.includes('sku') || h.includes('code'));
  const qtyIdx = header.findIndex(h => h.includes('qty') || h.includes('quantity') || h.includes('received'));
  if (skuIdx < 0 || qtyIdx < 0) return [];
  const receivedMap: Record<string, number> = {};
  for (const line of lines.slice(1)) {
    const cols = line.split(',').map(c => c.replace(/"/g, '').trim());
    const sku = cols[skuIdx];
    const qty = Number(cols[qtyIdx]);
    if (sku && !isNaN(qty)) receivedMap[sku] = qty;
  }
  return orderItems
    .filter(item => item.sku in receivedMap && receivedMap[item.sku] < item.qty)
    .map(item => ({
      sku: item.sku,
      productName: item.productName,
      orderedQty: item.qty,
      receivedQty: receivedMap[item.sku],
      shortageQty: item.qty - receivedMap[item.sku],
    }));
}

const TENANT_TYPE_COLOR: Record<string, string> = {
  RETAILER:    '#C17BAF',
  WHOLESALER:  '#7B61FF',
  DISTRIBUTOR: '#3DD68C',
  OTHERS:      '#9CA3AF',
};

const STATUS_MAP: Record<OrderStatus, { label: string; bg: string }> = {
  DRAFT:               { label: 'Draft',      bg: '#9ca3af' },
  PLACED:              { label: 'Pending',    bg: '#f97316' },
  VIEWED:              { label: 'Viewed',     bg: '#6366f1' },
  CONFIRMED:           { label: 'Confirmed',  bg: '#3b82f6' },
  PARTIALLY_FULFILLED: { label: 'Partial',    bg: '#f59e0b' },
  INVOICED:            { label: 'Billed',     bg: '#06b6d4' },
  DISPATCHED:          { label: 'Dispatched', bg: '#8b5cf6' },
  DELIVERED:           { label: 'Completed',  bg: '#22c55e' },
  REJECTED:            { label: 'Rejected',   bg: '#ef4444' },
  CANCELLED:           { label: 'Cancelled',  bg: '#9ca3af' },
};

function fmt(n: number) {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDt(s?: string) {
  if (!s) return '—';
  return new Date(s).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

const th: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7280',
  background: '#f3f4f6',
  borderBottom: '1px solid #e5e7eb',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 12,
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle',
};

interface ShortageEntry { sku: string; productName: string; orderedQty: number; receivedQty: number; shortageQty: number; }
interface OrderShortageRecord { orderId: number; shortages: ShortageEntry[]; }

export function MyOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [distName, setDistName] = useState('');
  const [distType, setDistType] = useState('');
  const [distCity, setDistCity] = useState('');
  const [distAddr, setDistAddr] = useState('');
  const [distGstin, setDistGstin] = useState('');
  const [distDl, setDistDl] = useState('');
  const [distPhone, setDistPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shortageRecord, setShortageRecord] = useState<OrderShortageRecord | null>(null);
  const [repeatDialogOpen, setRepeatDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [checkItemsBusy, setCheckItemsBusy] = useState(false);
  const [snack, setSnack] = useState('');
  const checkItemsRef = useRef<HTMLInputElement>(null);

  const orderId = Number(id);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const o = await ordersApi.get(orderId);
      setOrder(o);
      tenantApi.listPeers(undefined, 0, 500).then(res => {
        const match = res.content.find(p => p.id === o.distributorTenantId);
        if (match) {
          setDistName(match.name);
          setDistType(match.tenantType ?? '');
          setDistCity([match.city, match.state, match.pincode].filter(Boolean).join(', '));
          setDistAddr(match.address ?? '');
          setDistGstin(match.gstin ?? '');
          setDistDl(match.drugLicenseNumber ?? '');
          setDistPhone(match.phone ?? '');
        }
      }).catch(() => {});
    } catch {
      setError('Failed to load order details.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { void fetchOrder(); }, [fetchOrder]);

  // Load shortage data whenever order id changes
  useEffect(() => {
    ordersApi.getShortages(orderId)
      .then(shortages => setShortageRecord(shortages.length ? { orderId, shortages } : null))
      .catch(() => setShortageRecord(null));
  }, [orderId]);

  async function handleCancelOrder() {
    setCancelling(true);
    try {
      await ordersApi.cancel(orderId, cancelReason || undefined);
      setCancelDialogOpen(false);
      setCancelReason('');
      void fetchOrder();
    } catch {
      // stay open on error
    } finally {
      setCancelling(false);
    }
  }

  async function handleDownloadInvoice() {
    if (!order?.invoiceFileName) return;
    try {
      const blob = await ordersApi.downloadInvoice(orderId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = order.invoiceFileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silently ignore
    }
  }

  function handleRepeatConfirm() {
    if (!order) return;
    const items = order.items ?? [];
    const displayDist = distName || `Distributor #${order.distributorTenantId}`;
    navigate('/orders', {
      state: {
        repeatOrder: {
          orderNumber: order.orderNumber ?? String(order.id),
          distributorTenantId: order.distributorTenantId,
          distributorName: displayDist,
          items: items.map(i => ({
            productId: i.productId,
            productName: i.productName,
            sku: i.sku,
            uom: i.uom,
            qty: i.qty,
            ptr: i.unitPrice ?? i.ptr,
            unitPrice: i.unitPrice,
            mrp: i.mrp,
          })),
        },
        bookOrderTab: 1,
        ts: Date.now(),
      },
    });
    setRepeatDialogOpen(false);
  }

  async function handleCheckItemsFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !order) return;
    setCheckItemsBusy(true);
    try {
      const text = await file.text();
      const shortages = parseCheckItemsCsv(text, order.items ?? []);
      await ordersApi.recordShortages(orderId, shortages);
      setShortageRecord(shortages.length ? { orderId, shortages } : null);
      setSnack(shortages.length ? `${shortages.length} shortage(s) recorded.` : 'No shortages — all items match.');
    } catch {
      setSnack('Failed to process file.');
    } finally {
      setCheckItemsBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Order not found.'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  const statusDef = STATUS_MAP[order.status] ?? { label: order.status, bg: '#9ca3af' };
  const displayDist = distName || `Distributor #${order.distributorTenantId}`;
  const repName = order.salesRepName ?? (order.salesRepUserId ? `Rep #${order.salesRepUserId}` : undefined);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/orders')}
            sx={{ fontSize: 12, fontWeight: 600, color: '#374151', border: '1px solid #d1d5db', px: 1.5, py: 0.4, mb: 1, '&:hover': { bgcolor: '#f9fafb' } }}
          >
            BACK
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap' }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              Order To: {displayDist}
            </Typography>
            {repName && (
              <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.15, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #c4b5fd', color: '#7c3aed', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                👤 Via Rep: {repName}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(order.status === 'PLACED' || order.status === 'VIEWED') && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => setCancelDialogOpen(true)}
              sx={{ height: 28, fontSize: 11, fontWeight: 700, px: 2, borderRadius: '6px', textTransform: 'none', borderColor: '#fca5a5', color: '#dc2626', bgcolor: '#fff5f5', '&:hover': { bgcolor: '#fee2e2', borderColor: '#f87171' } }}
            >
              Cancel Order
            </Button>
          )}
          {order.invoiceFileName && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => void handleDownloadInvoice()}
              sx={{ height: 28, fontSize: 11, fontWeight: 700, px: 2, borderRadius: '6px', textTransform: 'none', borderColor: '#7B61FF', color: '#7B61FF', bgcolor: '#f5f3ff', '&:hover': { bgcolor: '#ede9fe', borderColor: '#6d28d9' } }}
            >
              Download Invoice
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ReplayIcon sx={{ fontSize: '13px !important' }} />}
            onClick={() => setRepeatDialogOpen(true)}
            sx={{ height: 28, fontSize: 11, fontWeight: 700, px: 2, borderRadius: '6px', textTransform: 'none', borderColor: '#86efac', color: '#16a34a', bgcolor: '#f0fdf4', '&:hover': { bgcolor: '#dcfce7', borderColor: '#4ade80' } }}
          >
            Repeat Order
          </Button>
          <Box sx={{ width: '1px', height: 24, bgcolor: '#e5e7eb', mx: 0.25 }} />

          <Tooltip title="Download CSV">
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => downloadCsv(order, displayDist)}
              sx={{ height: 28, fontSize: 11, fontWeight: 700, px: 2, borderRadius: '6px', textTransform: 'none', borderColor: '#d1d5db', color: '#374151', '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' } }}
            >
              CSV
            </Button>
          </Tooltip>
          <Tooltip title="Download Excel">
            <Button
              variant="outlined"
              startIcon={<GridOnIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => downloadExcel(order, displayDist)}
              sx={{ height: 28, fontSize: 11, fontWeight: 700, px: 2, borderRadius: '6px', textTransform: 'none', borderColor: '#d1d5db', color: '#374151', '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' } }}
            >
              Excel
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<PrintIcon sx={{ fontSize: '13px !important' }} />}
            onClick={() => window.print()}
            sx={{ height: 28, fontSize: 11, fontWeight: 700, px: 2, borderRadius: '6px', textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}
          >
            Print Order
          </Button>
          {order.status === 'DELIVERED' && (
            <Tooltip title="Upload CSV to check received quantities against order">
              <Button
                variant="outlined"
                startIcon={checkItemsBusy ? <CircularProgress size={12} /> : <CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
                disabled={checkItemsBusy}
                onClick={() => checkItemsRef.current?.click()}
                sx={{ height: 28, fontSize: 11, fontWeight: 700, px: 2, borderRadius: '6px', textTransform: 'none', borderColor: '#6366f1', color: '#6366f1', bgcolor: '#f5f3ff', '&:hover': { bgcolor: '#ede9fe', borderColor: '#4f46e5' } }}
              >
                Check Items
              </Button>
            </Tooltip>
          )}
          <input ref={checkItemsRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={e => void handleCheckItemsFile(e)} />
        </Box>
      </Box>
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />

      {/* Summary bar */}
      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', px: 2.5, py: 1.5, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fff', mb: 2, alignItems: 'center' }}>
        <Box>
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>ORDER #</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a56db' }}>{order.orderNumber || order.id}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>PRODUCTS</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{order.items?.length ?? 0}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>STATUS</Typography>
          <Box sx={{ display: 'inline-block', mt: 0.25, px: 1, py: 0.25, borderRadius: 0.5, bgcolor: statusDef.bg, color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>
            {statusDef.label.toUpperCase()}
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>ORDER TIME</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fmtDt(order.orderDate)}</Typography>
        </Box>
        {order.confirmedAt && (
          <Box>
            <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>CONFIRMED TIME</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fmtDt(order.confirmedAt)}</Typography>
          </Box>
        )}
        {order.deliveredAt && (
          <Box>
            <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>DELIVERED TIME</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fmtDt(order.deliveredAt)}</Typography>
          </Box>
        )}
      </Box>

      {order.cancelReason && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>
          <strong>Reason:</strong> {order.cancelReason}
        </Alert>
      )}

      {/* ── Supplier Details ── */}
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fff', px: 2.5, py: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', mb: 1, letterSpacing: 0.5 }}>
          SUPPLIER DETAILS
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Left: name + location */}
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {displayDist}
              {distType && (
                <Box component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: TENANT_TYPE_COLOR[distType] ?? TENANT_TYPE_COLOR.OTHERS }}>
                  {distType}
                </Box>
              )}
            </Typography>
            {distCity && (
              <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>📍 {distCity}</Typography>
            )}
            {distAddr && (
              <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>🏠 {distAddr}</Typography>
            )}
          </Box>
          {/* Right: compliance + contact */}
          {(distGstin || distDl || distPhone) && (
            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
              {distGstin && (
                <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: '#374151' }}>GST:</Box> {distGstin}
                </Typography>
              )}
              {distDl && (
                <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: '#374151' }}>DL No:</Box> {distDl}
                </Typography>
              )}
              {distPhone && (
                <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>📞 {distPhone}</Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Product table */}
      {(order.items ?? []).some(i => i.approxPrice) && (
        <Box sx={{ mb: 1.5, px: 2, py: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1, fontSize: 12, color: '#92400e' }}>
          ⚠️ Prices marked <strong>~</strong> are approximate (MRP × 80%) — supplier has not disclosed exact rates. Actuals may vary.
        </Box>
      )}
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 780, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[
                  ...(shortageRecord ? ['#', 'CODE', 'PRODUCT NAME', 'QTY', 'FREE', 'SCHEME', 'MRP', 'PTR RATE', 'DISCOUNT', 'GST', 'TOTAL (PTR)', 'SHORTAGE']
                                     : ['#', 'CODE', 'PRODUCT NAME', 'QTY', 'FREE', 'SCHEME', 'MRP', 'PTR RATE', 'DISCOUNT', 'GST', 'TOTAL (PTR)']),
                ].map(h => (
                  <th key={h} style={{ ...th, textAlign: ['#', 'QTY', 'FREE', 'MRP', 'PTR RATE', 'DISCOUNT', 'GST', 'TOTAL (PTR)', 'SHORTAGE'].includes(h) ? 'right' : 'left',
                    ...(h === 'SHORTAGE' ? { color: '#dc2626' } : {}) }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(order.items ?? []).map((item, idx) => (
                <tr key={item.id ?? idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ ...td, textAlign: 'right', color: '#9ca3af', width: 36 }}>{idx + 1}</td>
                  <td style={{ ...td, color: '#1a56db', fontWeight: 600 }}>{item.sku}</td>
                  <td style={{ ...td, minWidth: 200 }}><div style={{ fontWeight: 600, fontSize: 12 }}>{item.productName}</div></td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{item.qty}</td>
                  <td style={{ ...td, textAlign: 'right', color: Number(item.freeQty) > 0 ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>
                    {Number(item.freeQty) > 0 ? `+${item.freeQty}` : '—'}
                  </td>
                  <td style={{ ...td, color: '#7B61FF', fontWeight: 600 }}>{item.scheme || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{item.mrp != null ? `₹${Number(item.mrp).toFixed(2)}` : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: item.approxPrice ? '#b45309' : 'inherit' }}>
                    {item.approxPrice ? '~' : ''}₹{Number(item.unitPrice).toFixed(2)}
                    {item.approxPrice && <span style={{ fontSize: 10, marginLeft: 3, color: '#b45309' }}>(Approx.)</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>{Number(item.discountPct) > 0 ? `${item.discountPct}%` : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>{Number(item.gstRate) > 0 ? `${item.gstRate}%` : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: item.approxPrice ? '#b45309' : '#152346' }}>
                    {item.approxPrice ? '~' : ''}₹{(Number(item.lineTotal) - Number(item.gstAmt)).toFixed(2)}
                  </td>
                  {shortageRecord && (() => {
                    const s = shortageRecord.shortages.find(
                      sh => sh.sku === item.sku || sh.productName === item.productName
                    );
                    return (
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: s ? '#dc2626' : '#9ca3af' }}>
                        {s ? s.shortageQty : '—'}
                      </td>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#374151' }}>Totals</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                  {(order.items ?? []).reduce((s, i) => s + Number(i.qty), 0)}
                </td>
                <td colSpan={shortageRecord ? 8 : 7} />
              </tr>
            </tfoot>
          </table>
        </Box>
      </Box>

      {/* Amount summary */}
      {(() => {
        const netAmount = Number(order.subtotal) - Number(order.totalDiscount);
        const hasApprox = (order.items ?? []).some(i => i.approxPrice);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Box sx={{ minWidth: 300, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fff', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2.5, py: 1, borderBottom: '1px solid #f3f4f6' }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Net Amount (PTR){hasApprox ? ' (Approx.)' : ''}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: hasApprox ? '#b45309' : '#111827' }}>{hasApprox ? '~' : ''}₹{fmt(netAmount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2.5, py: 1.25, bgcolor: '#f8fafc' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346' }}>Bill Total (excl. GST){hasApprox ? ' (Approx.)' : ''}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: hasApprox ? '#b45309' : '#152346' }}>{hasApprox ? '~' : ''}₹{fmt(netAmount)}</Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* Cancel Order dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => { setCancelDialogOpen(false); setCancelReason(''); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Cancel Order</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#374151' }}>
            Cancel order <strong>#{order.orderNumber || order.id}</strong>? This cannot be undone.
          </Typography>
          <TextField
            fullWidth size="small" multiline rows={2}
            label="Reason (optional)"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button onClick={() => { setCancelDialogOpen(false); setCancelReason(''); }} size="small">No, Keep</Button>
          <Button
            variant="contained" size="small" disabled={cancelling}
            onClick={handleCancelOrder}
            sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
          >
            {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repeat Order confirmation dialog */}
      <Dialog open={repeatDialogOpen} onClose={() => setRepeatDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Repeat Order</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#374151' }}>
            Re-order the same <strong>{order.items?.length ?? 0} product{(order.items?.length ?? 0) !== 1 ? 's' : ''}</strong> from{' '}
            <strong>{distName || `Distributor #${order.distributorTenantId}`}</strong>?
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 1 }}>
            Order #{order.orderNumber || order.id} &nbsp;·&nbsp; ₹{fmt(Number(order.subtotal) - Number(order.totalDiscount))}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.75 }}>
            All products and quantities will be pre-filled in the Place Order tab. You can adjust before placing.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button onClick={() => setRepeatDialogOpen(false)} size="small">Cancel</Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleRepeatConfirm}
            sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
          >
            Yes, Repeat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
